import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Popconfirm } from 'antd';
import styled, { keyframes } from 'styled-components';
import { useDispatch } from 'react-redux';
import { setAuth } from '../redux/reducers/utils/authReducer';
import { persistor } from '../redux/configureStore';
import { PURGE } from 'redux-persist';

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  user-select: none;
`;

const VersionArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: pointer;
`;

const CurrentVersion = styled.div`
  font-size: 12px;
  color: #666;
  cursor: pointer;
`;

const ModalTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
`;

const ModalRow = styled.div`
  color: #555;
`;

const Changelog = styled.ul`
  margin: 8px 0 0;
  padding-left: 18px;
  color: #555;
`;

const SectionDivider = styled.div`
  margin: 16px 0;
  border-top: 1px solid #f0f0f0;
`;

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #333;
`;

const VersionList = styled.div`
  margin-top: 10px;
  max-height: 240px;
  overflow: auto;
  padding-right: 6px;
`;

const VersionItem = styled.div`
  margin-bottom: 10px;
`;

const VersionItemTitle = styled.div`
  font-weight: 600;
  color: #333;
`;

const VersionItemDate = styled.div`
  font-size: 12px;
  color: #8c8c8c;
`;

const ResetWarning = styled.div`
  margin-top: 10px;
  font-size: 13px;
  color: #8c8c8c;
`;

const LoaderContainer = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LoaderText = styled.div`
  font-size: 14px;
  color: #333;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background-color: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
`;

const progressAnimation = keyframes`
  0% { width: 0%; }
  100% { width: 100%; }
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #ff4d4f, #ff7875);
  border-radius: 3px;
  animation: ${progressAnimation} 3s ease-in-out infinite;
`;

const VERSION_STORAGE_KEY = 'appVersion';
const VERSION_PENDING_UPDATE_KEY = 'appVersionPendingUpdate';
const VERSION_RELOAD_REQUESTED_KEY = 'appVersionReloadRequestedTo';
const DEFAULT_POLL_INTERVAL_MS = 1 * 60 * 1000; // 1 минут

const parsePendingUpdate = (rawValue) => {
  if (!rawValue) return null;
  try {
    const parsed = JSON.parse(rawValue);
    if (!parsed?.to) return null;
    return {
      from: parsed?.from ? String(parsed.from) : null,
      to: String(parsed.to),
    };
  } catch {
    return null;
  }
};

const normalizeChangelog = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter(Boolean).map(String);
  if (typeof value === 'string') {
    return value
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
  }
  return [];
};

const compareBuildVersions = (left, right) => {
  const leftParts = String(left || '').split('.').map((part) => Number(part) || 0);
  const rightParts = String(right || '').split('.').map((part) => Number(part) || 0);
  const maxLength = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < maxLength; index += 1) {
    const leftValue = leftParts[index] ?? 0;
    const rightValue = rightParts[index] ?? 0;
    if (leftValue > rightValue) return 1;
    if (leftValue < rightValue) return -1;
  }

  return 0;
};

const VersionPanel = ({ pollIntervalMs = DEFAULT_POLL_INTERVAL_MS }) => {
  const dispatch = useDispatch();
  const isFirstSuccessfulCheckRef = useRef(true);

  const [releaseMeta, setReleaseMeta] = useState(null);
  const [currentBuild, setCurrentBuild] = useState('—');
  const [previousBuild, setPreviousBuild] = useState(null);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [pendingUpdate, setPendingUpdate] = useState(null);
  const [showReloadModal, setShowReloadModal] = useState(false);

  const [versionModalOpen, setVersionModalOpen] = useState(false);
  const [showAllChanges, setShowAllChanges] = useState(false);
  const [loadingChanges, setLoadingChanges] = useState(false);
  const [fetchedChanges, setFetchedChanges] = useState(null);

  const [isResetting, setIsResetting] = useState(false);
  const [resetStep, setResetStep] = useState('');

  useEffect(() => {
    let mounted = true;
    let intervalId = null;

    const load = async () => {
      setLoadingChanges(true);
      try {
        const resp = await fetch(`/release.json?t=${Date.now()}`, {
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' },
        });
        if (!resp.ok) throw new Error('network');
        const json = await resp.json();
        if (!mounted) return;
        setReleaseMeta(json);

        const builds = json?.builds;
        let list = [];
        if (builds && typeof builds === 'object') {
          list = Object.entries(builds)
            .sort((a, b) => compareBuildVersions(b[0], a[0]))
            .map(([build, meta]) => ({
              build,
              releasedAt: meta?.releasedAt || meta?.buildDate || 'не указана',
              changelog: normalizeChangelog(meta?.changelog),
            }));
        } else if (json?.default) {
          list = [
            {
              build: json?.default?.version || '—',
              releasedAt: json.default?.releasedAt || 'не указана',
              changelog: normalizeChangelog(json.default?.changelog),
            },
          ];
        }

        setFetchedChanges(list);

        if (list.length > 0) {
          const latest = list[0].build;
          const stored = localStorage.getItem(VERSION_STORAGE_KEY);
          const pendingRaw = localStorage.getItem(VERSION_PENDING_UPDATE_KEY);
          const pending = parsePendingUpdate(pendingRaw);
          const requestedReloadTo = sessionStorage.getItem(VERSION_RELOAD_REQUESTED_KEY);

          // Если до этого был обнаружен апдейт и выполнен reload,
          // показываем модалку уже после применения версии.
          if (
            pending
            && requestedReloadTo
            && compareBuildVersions(requestedReloadTo, pending.to) === 0
            && compareBuildVersions(latest, pending.to) >= 0
          ) {
            setCurrentBuild(pending.to);
            setPreviousBuild(pending.from || stored || null);
            setShowUpdateModal(true);
            setShowReloadModal(false);
            setPendingUpdate(null);

            try {
              localStorage.setItem(VERSION_STORAGE_KEY, pending.to);
              localStorage.removeItem(VERSION_PENDING_UPDATE_KEY);
              sessionStorage.removeItem(VERSION_RELOAD_REQUESTED_KEY);
            } catch {}

            isFirstSuccessfulCheckRef.current = false;
            return;
          }

          if (pending && compareBuildVersions(latest, pending.to) < 0) {
            try {
              localStorage.removeItem(VERSION_PENDING_UPDATE_KEY);
            } catch {}
            setPendingUpdate(null);
            setShowReloadModal(false);
          }

          if (!stored) {
            // Первый запуск на устройстве: фиксируем базовую версию без лишнего reload.
            setCurrentBuild(latest);
            try {
              localStorage.setItem(VERSION_STORAGE_KEY, latest);
            } catch {}
            isFirstSuccessfulCheckRef.current = false;
            return;
          }

          if (compareBuildVersions(latest, stored) > 0) {
            if (isFirstSuccessfulCheckRef.current) {
              // Приложение было закрыто и запущено уже с новой сборкой.
              // Показываем изменения без принудительной перезагрузки.
              setCurrentBuild(latest);
              setPreviousBuild(stored);
              setShowUpdateModal(true);
              setShowReloadModal(false);
              setPendingUpdate(null);
              try {
                localStorage.setItem(VERSION_STORAGE_KEY, latest);
                localStorage.removeItem(VERSION_PENDING_UPDATE_KEY);
                sessionStorage.removeItem(VERSION_RELOAD_REQUESTED_KEY);
              } catch {}
            } else {
              // Версия выросла во время активной сессии: сохраняем pending и просим reload,
              // чтобы подтянуть новые ассеты.
              const nextPending = { from: stored, to: latest };
              setCurrentBuild(stored);
              setPendingUpdate(nextPending);
              setShowReloadModal(true);
              try {
                localStorage.setItem(VERSION_PENDING_UPDATE_KEY, JSON.stringify(nextPending));
              } catch {}
            }

            isFirstSuccessfulCheckRef.current = false;
            return;
          }

          setCurrentBuild(stored);
          setPendingUpdate(null);
          setShowReloadModal(false);
          try {
            localStorage.removeItem(VERSION_PENDING_UPDATE_KEY);
            sessionStorage.removeItem(VERSION_RELOAD_REQUESTED_KEY);
          } catch {}
        }

        isFirstSuccessfulCheckRef.current = false;
      } catch (e) {
        // quietly ignore
      } finally {
        if (mounted) setLoadingChanges(false);
      }
    };

    // initial load
    load();

    // периодический опрос только если включён положительный интервал
    if (pollIntervalMs > 0) {
      intervalId = setInterval(() => {
        // не опрашиваем когда офлайн
        if (typeof navigator !== 'undefined' && !navigator.onLine) return;
        load();
      }, pollIntervalMs);
    }

    return () => {
      mounted = false;
      if (intervalId) clearInterval(intervalId);
    };
  }, [pollIntervalMs]);

  const changelog = useMemo(() => {
    if (!releaseMeta) return [];
    const meta = releaseMeta?.builds?.[currentBuild];
    const fromMeta = normalizeChangelog(meta?.changelog);
    if (fromMeta.length) return fromMeta;
    return normalizeChangelog(releaseMeta?.default?.changelog);
  }, [releaseMeta, currentBuild]);

  const allVersionChanges = useMemo(() => {
    if (Array.isArray(fetchedChanges) && fetchedChanges.length) return fetchedChanges;
    if (!releaseMeta) return [];

    const builds = releaseMeta?.builds;
    if (builds && typeof builds === 'object') {
      return Object.entries(builds)
        .sort((a, b) => compareBuildVersions(b[0], a[0]))
        .map(([build, meta]) => ({
          build,
          releasedAt: meta?.releasedAt || meta?.buildDate || 'не указана',
          changelog: normalizeChangelog(meta?.changelog),
        }));
    }

    return [
      {
        build: currentBuild,
        releasedAt: releaseMeta?.default?.releasedAt || 'не указана',
        changelog: normalizeChangelog(releaseMeta?.default?.changelog),
      },
    ];
  }, [fetchedChanges, releaseMeta, currentBuild]);

  const handleReset = async () => {
    setIsResetting(true);

    try {
      setResetStep('Сброс авторизации...');
      await new Promise((resolve) => setTimeout(resolve, 350));
      dispatch(setAuth(false));

      setResetStep('Очистка кэша приложения...');
      await new Promise((resolve) => setTimeout(resolve, 350));
      await persistor.purge();
      try { dispatch({ type: PURGE }); } catch (e) { /* noop */ }

      setResetStep('Очистка локального хранилища...');
      await new Promise((resolve) => setTimeout(resolve, 350));
      try {
        // Удаляем только чувствительные ключи, чтобы не потерять идентификатор устройства
        const keysToRemove = ['token', 'session'];
        keysToRemove.forEach((k) => {
          try { localStorage.removeItem(k); } catch (e) { /* noop */ }
        });
      } catch (e) {
        console.error('localStorage cleanup failed', e);
      }

      setResetStep('Перезагрузка приложения...');
      await new Promise((resolve) => setTimeout(resolve, 350));
      window.location.reload();
    } catch (error) {
      console.error('Ошибка при сбросе состояния:', error);
      setResetStep('Перезагрузка приложения...');
      await new Promise((resolve) => setTimeout(resolve, 350));
      window.location.reload();
    }
  };

  const handleReloadForUpdate = () => {
    const targetVersion = pendingUpdate?.to;
    if (!targetVersion) {
      window.location.reload();
      return;
    }

    try {
      sessionStorage.setItem(VERSION_RELOAD_REQUESTED_KEY, targetVersion);
    } catch {}

    // query-параметры помогают обойти промежуточный кеш index.html в браузере.
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('build', targetVersion);
    currentUrl.searchParams.set('ts', String(Date.now()));
    window.location.replace(currentUrl.toString());
  };

  return (
    <Wrap>
      <VersionArea>
        <CurrentVersion onClick={() => setVersionModalOpen(true)}>v:{currentBuild}</CurrentVersion>
      </VersionArea>

      <Modal
        open={versionModalOpen}
        onCancel={() => setVersionModalOpen(false)}
        centered
        footer={[
          <Button key="close" onClick={() => setVersionModalOpen(false)}>
            Закрыть
          </Button>,
        ]}
      >
        <ModalTitle>Версия приложения</ModalTitle>
        <ModalRow>
          Текущая версия: <b>{currentBuild}</b>
        </ModalRow>

        <Button
          type="link"
          style={{ paddingLeft: 0 }}
          onClick={async () => {
            const next = !showAllChanges;
            setShowAllChanges(next);
          }}
        >
          {showAllChanges ? 'Скрыть изменения' : 'Посмотреть изменения'}
        </Button>

        {showAllChanges && (
          <VersionList>
            {loadingChanges ? (
              <ModalRow>Загрузка изменений...</ModalRow>
            ) : (
              (() => {
                const list = allVersionChanges;
                if (!list || !list.length) return <ModalRow>Изменения не найдены в конфиге.</ModalRow>;
                return list.map((versionMeta) => (
                  <VersionItem key={versionMeta.build}>
                    <VersionItemTitle>v: {versionMeta.build}</VersionItemTitle>
                    <VersionItemDate>Сборка от: {versionMeta.releasedAt}</VersionItemDate>
                    {versionMeta.changelog?.length ? (
                      <Changelog>
                        {versionMeta.changelog.map((item, index) => (
                          <li key={`${versionMeta.build}-${index}`}>{item}</li>
                        ))}
                      </Changelog>
                    ) : (
                      <ModalRow>— без описания изменений —</ModalRow>
                    )}
                  </VersionItem>
                ));
              })()
            )}
          </VersionList>
        )}

        <SectionDivider />
        <SectionTitle>Сброс приложения</SectionTitle>
        {!isResetting ? (
          <>
            <ResetWarning>
              Полностью очищает авторизацию и локальные данные, затем перезапускает приложение.
            </ResetWarning>
            <Popconfirm
              title="Вы уверены? Это удалит авторизацию и локальные данные и перезагрузит приложение."
              onConfirm={handleReset}
              okText="Да, сбросить"
              cancelText="Отмена"
              disabled={isResetting}
            >
              <Button danger style={{ marginTop: 12 }} disabled={isResetting}>
                Сбросить приложение
              </Button>
            </Popconfirm>
          </>
        ) : (
          <LoaderContainer>
            <LoaderText>{resetStep}</LoaderText>
            <ProgressBar>
              <ProgressFill />
            </ProgressBar>
          </LoaderContainer>
        )}
      </Modal>

      <Modal
        open={showUpdateModal}
        onCancel={() => setShowUpdateModal(false)}
        centered
        footer={[
          <Button key="ok" type="primary" onClick={() => setShowUpdateModal(false)}>
            ОК
          </Button>,
        ]}
      >
        <ModalTitle>Приложение обновилось</ModalTitle>
        <ModalRow>
          Версия: <b>{previousBuild || '—'}</b> ➜ <b>{currentBuild}</b>
        </ModalRow>
        {changelog?.length ? (
          <>
            <ModalRow style={{ marginTop: 8 }}>Изменения:</ModalRow>
            <Changelog>
              {changelog.map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </Changelog>
          </>
        ) : (
          <ModalRow style={{ marginTop: 8 }}>Описание изменений пока не добавлено.</ModalRow>
        )}
      </Modal>

      <Modal
        open={showReloadModal}
        onCancel={() => setShowReloadModal(false)}
        centered
        footer={[
          <Button key="later" onClick={() => setShowReloadModal(false)}>
            Позже
          </Button>,
          <Button key="reload" type="primary" onClick={handleReloadForUpdate}>
            Обновить сейчас
          </Button>,
        ]}
      >
        <ModalTitle>Доступно обновление приложения</ModalTitle>
        <ModalRow>
          Версия: <b>{pendingUpdate?.from || currentBuild}</b> ➜ <b>{pendingUpdate?.to || '—'}</b>
        </ModalRow>
        <ModalRow style={{ marginTop: 8 }}>
          Чтобы загрузить новые файлы, выполните обновление страницы.
        </ModalRow>
      </Modal>
    </Wrap>
  );
};

export default VersionPanel;
